import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { useEstimationWizard } from '@/context/estimationWizardContext';
import { useLocationMatch } from '@/hooks/useLocationMatch';

interface EstimationNavigationItemWithoutParentProps {
  isActive: boolean;
  label: string;
  index: number;
  navigateTo?: string;
  disabled: boolean;
  isOmitted?: boolean;
  hasChildren: boolean;
  isChild?: false;
  parentIndex?: never;
  ignoreNumbering?: boolean;
  stepOffset?: number;
  parentIndexOffset?: number;
  onItemClick?: () => void;
}

interface EstimationNavigationItemWithParentProps {
  label: string;
  index: number;
  navigateTo?: string;
  disabled: boolean;
  isOmitted?: boolean;
  hasChildren: boolean;
  isChild: true;
  parentIndex: number;
  isActive?: false;
  ignoreNumbering?: boolean;
  stepOffset?: number;
  parentIndexOffset?: number;
  onItemClick?: () => void;
}

export type EstimationNavigationItemProps =
  | EstimationNavigationItemWithoutParentProps
  | EstimationNavigationItemWithParentProps;

export const EstimationNavigationItem: FC<EstimationNavigationItemProps> = ({
  isActive,
  ignoreNumbering,
  label,
  index,
  navigateTo,
  disabled,
  isOmitted,
  isChild,
  hasChildren,
  parentIndex,
  stepOffset = 0,
  parentIndexOffset = 0,
  onItemClick,
}) => {
  const {
    actions: { setActiveStep },
  } = useEstimationWizard();

  const [isActiveItem, setIsActiveItem] = useState(false);
  const { hasMatch } = useLocationMatch();

  useEffect(() => {
    setIsActiveItem(hasMatch(navigateTo));
  }, [hasMatch, navigateTo]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (disabled || isOmitted) {
        event.preventDefault();
        return;
      }
      if (isChild) {
        setActiveStep([parentIndex + parentIndexOffset, index]);
      } else {
        setActiveStep([index + parentIndexOffset, hasChildren ? 0 : undefined]);
      }

      // Call onItemClick to collapse sidebar after navigation
      onItemClick?.();
    },
    [
      disabled,
      isOmitted,
      isChild,
      setActiveStep,
      parentIndex,
      index,
      hasChildren,
      parentIndexOffset,
      onItemClick,
    ],
  );

  return (
    <Link
      disabled={disabled || isOmitted}
      onClick={(event) => handleClick(event)}
      to={navigateTo}
      data-testid={label}
      className={`${isActiveItem || isActive ? 'bg-(--nav-item-bg) text-(--primary)' : 'text-(--text-secondary)'} ${isChild ? 'pl-8' : undefined} hover:color-primary mb-2 flex w-full rounded-md text-sm font-semibold hover:bg-(--nav-item-bg) ${isOmitted ? 'cursor-not-allowed text-slate-600 line-through' : disabled ? 'cursor-default text-slate-600' : 'cursor-pointer'}`}
    >
      {!ignoreNumbering ? (
        <p className='px-3 py-[6px]'>{`${index + stepOffset + 1}. ${label}`}</p>
      ) : (
        <p className='px-3 py-[6px]'>{label}</p>
      )}
    </Link>
  );
};
